import { useState } from 'react';
import { ReactComponent as TrashIcon } from '$icons/TrashIcon.svg';
import Button from '$components/Buttons/Button';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import {
  useDeleteCandidateMutation,
  useGetCandidateDeletionPermissionQuery,
} from '$store/modules/candidates';
import { Candidate } from '../../../../types/candidates';

type DeleteCandidateProps = {
  selectedCandidates: string[];
  candidates: Candidate[];
  onDeleted: () => void;
};

// Only rendered for the internal users the backend allows to delete candidates.
const DeleteCandidate = ({
  selectedCandidates,
  candidates,
  onDeleted,
}: DeleteCandidateProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: permission } = useGetCandidateDeletionPermissionQuery();
  const [deleteCandidate, { isLoading }] = useDeleteCandidateMutation();

  if (!permission?.allowed) {
    return null;
  }

  // One candidate at a time: the button stays disabled for zero or several selected.
  const candidateId =
    selectedCandidates.length === 1 ? Number(selectedCandidates[0]) : null;
  const candidate = candidates.find((row) => row.candidate_Id === candidateId);
  const candidateLabel = candidate
    ? `${candidate.user_FirstName} ${candidate.user_LastName} (${candidate.user_Email})`
    : `#${candidateId}`;

  const closeModal = () => setIsModalOpen(false);

  const deleteCandidateHandler = () => {
    if (candidateId === null) return;
    deleteCandidate({ candidateId }).then((response) => {
      if (!('error' in response)) {
        onDeleted();
        closeModal();
      }
    });
  };

  return (
    <>
      <Button
        bgColor="#c0392b"
        showText
        text="Delete Candidate"
        icon={TrashIcon}
        iconStyles={{ fill: 'white' }}
        invertTextContent
        styling="px-4"
        onClick={() => setIsModalOpen(true)}
        disabled={candidateId === null}
      />
      <ModalInTailwind isOpen={isModalOpen} closeModal={closeModal}>
        <div className="bg-white rounded p-8 flex flex-col">
          <h1 className="font-bold text-xl mb-4">
            Are you sure you want to delete this candidate?
          </h1>
          <span>
            Candidate {candidateId}: <strong>{candidateLabel}</strong>
          </span>
          <span className="mt-2">
            The candidate and their user will be marked as deleted and will no
            longer appear in the platform.
          </span>
          <div className="flex flex-row mt-4 self-end w-60">
            <Button
              text="Cancel"
              showText
              textStyles={{ color: '#079e88' }}
              onClick={closeModal}
            />
            <Button
              text={isLoading ? 'Deleting' : 'Delete'}
              showText
              disabled={isLoading}
              bgColor={isLoading ? '#aaa' : '#c0392b'}
              onClick={deleteCandidateHandler}
            />
          </div>
        </div>
      </ModalInTailwind>
    </>
  );
};

export default DeleteCandidate;
